type FrontendExpectedErrorRule = {
  code?: string
  codePattern?: RegExp
  operationPattern?: RegExp
}

type FrontendExpectedErrorsConfig = {
  common: FrontendExpectedErrorRule[]
  // The key is the URL path segment extracted by getDomainFromPathname().
  // e.g. /partners/:id/warehouses/:id/<domain>/... → 'campaigns'
  // This is the route segment, NOT the domains/ folder name.
  byDomain: Record<string, FrontendExpectedErrorRule[]>
}

export const expectedErrorsConfig: FrontendExpectedErrorsConfig = {
  common: [{ code: 'UNDETERMINED_ACCOUNT' }],
  byDomain: {
    campaigns: [
      // Backend validation errors (budget limits, spend validation, etc.) from CreateValueGrantPolicy
      // are expected business logic — the UI shows a validation modal and retries with skip-validation.
      { codePattern: /_validation/i, operationPattern: /ValueGrantPolicy/ },
    ],
    'orders-operational-dashboard': [
      // Boba OfferService rejects unenrolled retailers with INVALID_ARGUMENT. The UI gracefully
      // falls back to empty discount data, so this is expected business logic, not an error.
      { code: 'INVALID_ARGUMENT', operationPattern: /GetOrderDiscountInfo/ },
      // ConnectPlatform returns NOT_FOUND for orders whose config IDs don't map to a known partner
      // (e.g. retailers not in RETAILER_APP_ID_MAP, or older orders with null config IDs).
      // Non-admin users get NOT_AUTHORIZED (fail-closed) — expected business logic, not a bug.
      {
        code: 'NOT_AUTHORIZED',
        operationPattern:
          /getPaymentMethodSummary|getOrderAmountsSummaryV0|getOrderStateChangeListMesh/,
      },
    ],
    'account-management': [
      // Duplicate email errors from enterpriseAccountCreate and enterpriseAccountCreateNoPartnerAccess.
      // The UI shows a validation message — expected business logic, not a bug.
      { code: 'CONFLICT', operationPattern: /createEnterpriseAccount/ },
      // Acting user lacks the access level required to create the target account
      // (AppError::Accounts::InsufficientAccessLevel). Permission-gating business rule,
      // surfaced to the user as a validation message.
      { code: 'INSUFFICIENT_ACCESS_LEVEL', operationPattern: /createEnterpriseAccount/ },
      // Acting user tried to grant a role they themselves cannot attain
      // (AppError::Accounts::RoleNotAttainable). The UI surfaces this as a validation
      // message — expected business logic, not a bug.
      { code: 'ROLE_NOT_ATTAINABLE_FOR_ACCOUNT', operationPattern: /createEnterpriseAccount/ },
      // Acting user tried to assign a SOX-restricted role or permission
      // (AppError::Accounts::SoxSensitiveRoleAssignment). Code is custom and unique
      // to this error class, so it is impossible for the rule to mask anything else;
      // the operationPattern is defensive scoping to the real role-assignment mutations
      // (create-on-account and the role-update flows).
      {
        code: 'SOX_SENSITIVE_ROLE_ASSIGNMENT',
        operationPattern:
          /createEnterpriseAccount|enterpriseAccountCreate|enterpriseAccountUpdateRoles|updateEnterpriseAccount/,
      },
    ],
    'storefront-onboarding': [
      // Duplicate email error from the create-partner-admin wizard.
      // The UI shows a validation message — expected business logic, not a bug.
      { code: 'CONFLICT', operationPattern: /createEnterpriseAccount/ },
      // Acting user lacks the access level required to create the target account
      // (AppError::Accounts::InsufficientAccessLevel). Permission-gating business rule,
      // surfaced to the user as a validation message.
      { code: 'INSUFFICIENT_ACCESS_LEVEL', operationPattern: /createEnterpriseAccount/ },
      // Acting user tried to grant a role they themselves cannot attain
      // (AppError::Accounts::RoleNotAttainable). The UI surfaces this as a validation
      // message — expected business logic, not a bug.
      { code: 'ROLE_NOT_ATTAINABLE_FOR_ACCOUNT', operationPattern: /createEnterpriseAccount/ },
      // Acting user tried to assign a SOX-restricted role or permission
      // (AppError::Accounts::SoxSensitiveRoleAssignment). Code is custom and unique
      // to this error class, so it is impossible for the rule to mask anything else;
      // the operationPattern is defensive scoping to the real role-assignment mutations.
      {
        code: 'SOX_SENSITIVE_ROLE_ASSIGNMENT',
        operationPattern: /createEnterpriseAccount|enterpriseAccountCreate/,
      },
    ],
    customers: [
      // ConnectPlatform returns NOT_FOUND for orders whose config IDs don't map to a known partner.
      // The Mesh authorization handler catches this and fails-closed (unauthorized) — the error in
      // the GraphQL response is expected business logic, not a bug.
      { code: 'NOT_FOUND', operationPattern: /ListOrders/ },
    ],
    onboarding: [
      // getStoreHoursBulk proxies to the upstream availability service, which uses a strict
      // RetailerLocation.find() and returns NOT_FOUND when a store's external id has not yet
      // synced into that service (replication lag). Store hours are a supplementary "completed"
      // indicator and the table degrades gracefully — expected, and not fixable from the FE.
      // (NOT a NOT_AUTHORIZED suppression: permission gating is enforced client-side before the
      // query fires — see OnboardingStoreTable's warehouse_location_hours.view check.)
      { code: 'NOT_FOUND', operationPattern: /getStoreHoursBulk/ },
    ],
    'site-management': [
      // CustomersContentManagementAdmin returns NOT_FOUND when a store configuration has no
      // native tab bar navigation configured yet. The UI handles this by showing the empty
      // editor state — expected business logic, not a bug.
      { code: 'NOT_FOUND', operationPattern: /getTabBarNavigation/ },
    ],
    massops: [
      // INVALID_ARGUMENT: CSV validation failures (e.g. missing required headers, bad row data).
      // NOT_FOUND: intervention or resource not found — expected when navigating to a stale link.
      // FAILED_PRECONDITION: intervention is not in a valid state for the requested operation
      // (e.g. approval flow state machine transitions). All three are expected business logic.
      { code: 'INVALID_ARGUMENT' },
      { code: 'NOT_FOUND' },
      { code: 'FAILED_PRECONDITION' },
    ],
    'catalog-admin': [
      // Optimistic-lock conflict on audit task/batch updates when the FE submits a stale version;
      // handled explicitly by the FE (CAT-66597).
      {
        code: 'RECORD_VERSION_CONFLICT',
        operationPattern: /demeterServiceUpdateAuditTask|demeterServiceUpdateAuditBatch/,
      },
    ],
  },
}
